import { useEffect, useState } from "react";
import Cropper from "react-easy-crop";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";

const loadSrc = async (src) => {
  let blob;
  if (src.startsWith("/api/")) blob = (await api.get(src.slice(4), { responseType: "blob" })).data;
  else if (/^https?:\/\//.test(src) && !src.startsWith(window.location.origin)) blob = (await api.get("/admin/image-proxy", { params: { url: src }, responseType: "blob" })).data;
  else { const r = await fetch(src); if (!r.ok) throw new Error("load"); blob = await r.blob(); }
  const bmp = await createImageBitmap(blob);
  const scale = Math.min(1, MAX_SIDE / Math.max(bmp.width, bmp.height));
  if (scale === 1) return URL.createObjectURL(blob);
  const c = document.createElement("canvas");
  c.width = Math.round(bmp.width * scale); c.height = Math.round(bmp.height * scale);
  c.getContext("2d").drawImage(bmp, 0, 0, c.width, c.height);
  const small = await new Promise((res) => c.toBlob(res, "image/jpeg", 0.92));
  return URL.createObjectURL(small);
};
const MAX_SIDE = 1600;

const cropToBlob = (src, area) => new Promise((resolve, reject) => {
  const img = new Image();
  img.onload = () => {
    const c = document.createElement("canvas");
    const w = Math.min(1600, Math.round(area.width));
    c.width = w; c.height = Math.round(w * 3 / 4);
    const ctx = c.getContext("2d");
    const k = c.width / area.width;
    ctx.fillStyle = "#ffffff"; ctx.fillRect(0, 0, c.width, c.height);
    ctx.drawImage(img, -area.x * k, -area.y * k, img.naturalWidth * k, img.naturalHeight * k);
    c.toBlob((b) => (b ? resolve(b) : reject(new Error("crop"))), "image/jpeg", 0.9);
  };
  img.onerror = reject;
  img.src = src;
});

export const CropDialog = ({ open, onOpenChange, src, onCropped, uploadPath = "/admin/upload" }) => {
  const [local, setLocal] = useState(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [area, setArea] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open || !src) return;
    setLocal(null); setZoom(1); setCrop({ x: 0, y: 0 });
    loadSrc(src).then(setLocal).catch(() => { toast.error("Impossible de charger la photo pour la recadrer"); onOpenChange(false); });
  }, [open, src]); // eslint-disable-line react-hooks/exhaustive-deps

  const save = async () => {
    setBusy(true);
    try {
      const blob = await cropToBlob(local, area);
      const fd = new FormData();
      fd.append("file", blob, "recadrage.jpg");
      const { data } = await api.post(uploadPath, fd, { headers: { "Content-Type": "multipart/form-data" } });
      onCropped(data.url);
      onOpenChange(false);
      toast.success("Photo recadrée");
    } catch { toast.error("Échec du recadrage"); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="crop-dialog">
        <DialogHeader><DialogTitle>Recadrer la photo (format 4:3)</DialogTitle></DialogHeader>
        <div className="relative w-full h-[55vh] bg-white border border-border rounded-md overflow-hidden">
          {local ? (
            <Cropper image={local} crop={crop} zoom={zoom} minZoom={0.3} maxZoom={4} aspect={4 / 3} restrictPosition={false}
              onCropChange={setCrop} onZoomChange={setZoom} onCropComplete={(_, px) => setArea(px)} objectFit="contain" />
          ) : <div className="absolute inset-0 grid place-items-center text-muted-foreground"><Loader2 className="w-6 h-6 animate-spin" /></div>}
        </div>
        <div className="flex items-center gap-3">
          <span className="text-xs text-muted-foreground w-12">Zoom</span>
          <Slider value={[zoom]} min={0.3} max={4} step={0.05} onValueChange={(v) => setZoom(v[0])} data-testid="crop-zoom" />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="crop-cancel">Annuler</Button>
          <Button onClick={save} disabled={!local || !area || busy} data-testid="crop-save">
            {busy && <Loader2 className="w-4 h-4 animate-spin mr-1" />} Enregistrer le recadrage
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
